import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { catalog } from '../api/index.js';
import { Card } from '../components/ui/card.js';
import { Loader2, Search, AlertCircle, Users, UserCircle } from 'lucide-react';

const SENTINEL = 'ig.field.type.delete';
function imgUrl(v: unknown): string | undefined {
  if (!v || v === SENTINEL) return undefined;
  if (typeof v === 'string') return v || undefined;
  if (typeof v === 'object') {
    const o = v as any;
    return o.url ?? o.src ?? undefined;
  }
  return undefined;
}

export function ContributorList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  function handleSearch(value: string) {
    setSearch(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const t = setTimeout(() => setDebouncedSearch(value), 400);
    setDebounceTimer(t);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog-contributors', debouncedSearch],
    queryFn: () => catalog.contributors(debouncedSearch || undefined, 250),
    staleTime: 5 * 60 * 1000,
  });

  const contributors = data?.contributors ?? [];

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users size={22} className="text-brand-600" /> Cast & Crew
        </h1>
        <p className="text-gray-500 text-sm mt-1">{contributors.length} contributors{debouncedSearch ? ' matching search' : ''}</p>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search contributors…"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16 text-red-500 gap-2 text-sm">
            <AlertCircle size={16} /> Failed to load contributors
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {contributors.map(c => {
                  const image = imgUrl((c as any).image);
                  return (
                    <tr
                      key={c._id}
                      onClick={() => navigate(`/contributors/${c._id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {image ? (
                            <img
                              src={image}
                              alt=""
                              className="w-9 h-9 rounded-full object-cover bg-gray-100 flex-shrink-0"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <UserCircle size={18} className="text-gray-400" />
                            </div>
                          )}
                          <span className="font-medium text-gray-900">{c.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs font-mono">{c._id}</td>
                    </tr>
                  );
                })}
                {contributors.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-5 py-12 text-center text-gray-400">
                      {debouncedSearch ? 'No contributors match the search' : 'No contributors found — try syncing the catalog'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
